/*
  TrendDrop — collection & search UI (COMMERCE).
  Works alongside the core facets.js (filtering/sorting via the Section Rendering API):
  - [data-td-facets-open]  → opens the filter popover / bottom sheet (<dialog data-td-sheet>)
  - grid/list toggle, floating Filter/Categories pills (categories sheet is a <dialog data-td-sheet>)
  - [data-td-sort] → custom "Sort by" listbox built from radio inputs
  - <td-price-slider> → dual-handle range synced to the core price text inputs
  - <td-load-more> → AJAX "Load more" that appends the next page of #product-grid
  All behaviour uses event delegation so it survives facets.js re-renders.
*/
(() => {
  if (window.__tdCollectionInit) return;
  window.__tdCollectionInit = true;

  const sheet = () => window.TDCommerce;

  /* ---------- Filter popover ---------- */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-td-facets-open]');
    if (!opener) return;
    const dialog = document.getElementById(opener.dataset.tdFacetsOpen);
    if (!dialog || !sheet()) return;
    event.preventDefault();
    closeAllSorts();
    sheet().openSheet(dialog, opener);
  });

  /* ---------- Sort listbox ---------- */
  function closeSort(toggle, focusToggle = false) {
    if (!toggle) return;
    const list = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.setAttribute('aria-expanded', 'false');
    if (list) list.hidden = true;
    if (focusToggle) toggle.focus();
  }
  function closeAllSorts() {
    document.querySelectorAll('[data-td-sort-toggle][aria-expanded="true"]').forEach((t) => closeSort(t));
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-td-sort-toggle]');
    if (toggle) {
      const list = document.getElementById(toggle.getAttribute('aria-controls'));
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      closeAllSorts();
      if (open && list) {
        toggle.setAttribute('aria-expanded', 'true');
        list.hidden = false;
        (list.querySelector('input:checked') || list.querySelector('input'))?.focus();
      }
      return;
    }
    if (!event.target.closest('.td-sort__list')) closeAllSorts();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const list = event.target.closest?.('.td-sort__list');
    if (!list) return;
    const toggle = document.querySelector(`[aria-controls="${list.id}"]`);
    closeSort(toggle, true);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.matches('[data-td-sort] input[type="radio"]')) return;
    const form = input.closest('[data-td-sort]');
    const label = form.querySelector('[data-td-sort-current]');
    if (label) label.textContent = input.closest('label')?.textContent.trim() || input.value;
    form.querySelectorAll('.td-sort__option').forEach((o) => o.classList.toggle('is-active', o.contains(input)));
    closeSort(form.querySelector('[data-td-sort-toggle]'), true);
  });

  /* ---------- Dual price slider ---------- */
  if (!customElements.get('td-price-slider')) {
    class TdPriceSlider extends HTMLElement {
      connectedCallback() {
        this.minRange = this.querySelector('.td-price-slider__range--min');
        this.maxRange = this.querySelector('.td-price-slider__range--max');
        if (!this.minRange || !this.maxRange) return;
        this.max = Number(this.dataset.max) || Number(this.maxRange.max) || 0;
        this.onRange = this.onRange.bind(this);
        this.onField = this.onField.bind(this);
        this.minRange.addEventListener('input', this.onRange);
        this.maxRange.addEventListener('input', this.onRange);
        this.fields.forEach((f) => f && f.addEventListener('change', this.onField));
        this.paint();
      }
      disconnectedCallback() {
        this.fields.forEach((f) => f && f.removeEventListener('change', this.onField));
      }
      get fields() {
        return [document.getElementById(this.dataset.targetMin), document.getElementById(this.dataset.targetMax)];
      }
      static parse(value) {
        if (!value) return NaN;
        // accept "1,234.50", "1.234,50", "1 234"
        const cleaned = String(value).replace(/\s/g, '');
        const lastSep = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
        if (lastSep === -1) return Number(cleaned);
        const decimals = cleaned.length - lastSep - 1;
        if (decimals === 3) return Number(cleaned.replace(/[.,]/g, ''));
        return Number(cleaned.slice(0, lastSep).replace(/[.,]/g, '') + '.' + cleaned.slice(lastSep + 1));
      }
      onRange(event) {
        let min = Number(this.minRange.value);
        let max = Number(this.maxRange.value);
        if (min > max) {
          if (event.target === this.minRange) this.minRange.value = min = max;
          else this.maxRange.value = max = min;
        }
        const [minField, maxField] = this.fields;
        if (minField) minField.value = min > 0 ? String(min) : '';
        if (maxField) maxField.value = max < this.max ? String(max) : '';
        this.paint();
      }
      onField() {
        const [minField, maxField] = this.fields;
        const min = TdPriceSlider.parse(minField?.value);
        const max = TdPriceSlider.parse(maxField?.value);
        this.minRange.value = Number.isNaN(min) ? 0 : min;
        this.maxRange.value = Number.isNaN(max) ? this.max : max;
        this.paint();
      }
      paint() {
        const span = this.max || 1;
        this.style.setProperty('--td-min', `${(Number(this.minRange.value) / span) * 100}%`);
        this.style.setProperty('--td-max', `${(Number(this.maxRange.value) / span) * 100}%`);
      }
    }
    customElements.define('td-price-slider', TdPriceSlider);
  }

  /* ---------- Load more ---------- */
  if (!customElements.get('td-load-more')) {
    class TdLoadMore extends HTMLElement {
      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
      }
      async onClick(event) {
        const button = event.target.closest('[data-td-load-more]');
        if (!button || this.loading || !sheet()) return;
        event.preventDefault();
        this.loading = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        const grid = document.getElementById('product-grid');
        const startCount = grid ? grid.children.length : 0;
        try {
          const doc = await sheet().fetchSectionHTML(button.dataset.tdLoadMore || button.href, this.dataset.sectionId);
          const items = doc.querySelectorAll('#product-grid > li');
          items.forEach((li) => {
            li.classList.add('td-product-grid__item--new');
            grid.appendChild(document.adoptNode(li));
          });
          const nextPager = doc.querySelector('td-load-more');
          if (nextPager) {
            this.innerHTML = nextPager.innerHTML;
            this.dataset.appended = 'true';
          } else {
            this.remove();
          }
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          document.dispatchEvent(new CustomEvent('td:products:appended', { detail: { grid } }));
          const firstNew = grid?.children[startCount]?.querySelector('a[href]');
          firstNew?.focus({ preventScroll: true });
        } catch (error) {
          console.error(error);
          window.location.href = button.href;
        } finally {
          this.loading = false;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
      }
    }
    customElements.define('td-load-more', TdLoadMore);
  }

  /* facets.js re-rendered: reset sort toggles, announce the new "Show results (N)" label */
  document.addEventListener('td:facets:rendered', () => {
    document.querySelectorAll('[data-td-sort-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.querySelectorAll('[data-td-facets-live]').forEach((live) => {
      const label = live.closest('form')?.querySelector('.td-facets__apply')?.textContent.trim();
      if (label) live.textContent = label;
    });
  });

  /* ---------- Grid / list toggle (remembered per browser) ---------- */
  const VIEW_KEY = 'td-collection-view';
  const readView = () => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
    } catch (e) {
      return 'grid';
    }
  };
  const applyView = (view) => {
    document.querySelectorAll('[data-td-view-root]').forEach((root) => (root.dataset.view = view));
    document.querySelectorAll('[data-td-view]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.tdView === view));
    });
  };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-td-view]');
    if (!btn) return;
    const view = btn.dataset.tdView === 'list' ? 'list' : 'grid';
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch (e) {}
    applyView(view);
  });

  /* ---------- Saved grid / list view (the floating filter pills were removed; their observer went with them) ---------- */
  const initView = () => applyView(readView());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initView);
  else initView();
  document.addEventListener('shopify:section:load', initView);
})();
